import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQueryWithReauth } from './baseQuery';
import { GetItemTrackerParams, ItemTrackerItem, UpdateItemTrackerParams, GetAllItemTrackersByDateRangeParams} from '../types/itemTracker';
import { DataDTOPagedDataDataDTOItemTrackerWithProduct } from '../types/api/generated/api';

export const itemTrackerAPI = createApi({
    reducerPath: 'itemTrackerAPI',
    baseQuery: axiosBaseQueryWithReauth({
        baseUrl: process.env.REACT_APP_API_URL
    }),
    tagTypes: ['ItemTracker'],
    endpoints: (builder) => ({
        getItemTrackers: builder.query<ItemTrackerItem[],GetItemTrackerParams>({
            query: ({ storeId, seconds, status }) => ({
                url: `ItemTrackers/store/${storeId}/latest`,
                method: 'GET',
                params: {
                    seconds,
                    status
                },
            }),
            providesTags: [{ type: 'ItemTracker', id: 'LIVE' }]
        }),
        getAllItemTrackers: builder.query<DataDTOPagedDataDataDTOItemTrackerWithProduct,GetAllItemTrackersByDateRangeParams>({
            query: ({page, pageSize, orderBy, searchTerm, filters, modifiedAfter, storeId, startDate, endDate, intelligencePointId}) => ({
                url: `ItemTrackers/store/${storeId}/GetAllByDateRange`,
                method: 'GET',
                params:{
                    page,
                    pageSize,
                    orderBy: orderBy,
                    searchTerm,
                    filters: JSON.stringify(filters),
                    modifiedAfter,
                    startDate,
                    endDate,
                    intelligencePointId
                }

            }),
            providesTags: [{ type: 'ItemTracker', id: 'LIST' }]
        }),
        updateItemTracker: builder.mutation<Omit<UpdateItemTrackerParams, 'id'>, UpdateItemTrackerParams & { queryParams?: GetItemTrackerParams }>({
            query: (item) => ({
                url: `ItemTrackers/${item.id}`,
                method: 'PUT',
                body: {
                    disposition: item.disposition,
                    notes: item.notes,
                    isHidden: item.isHidden,
                }
            }),
            async onQueryStarted(item, { dispatch, queryFulfilled }) {
                // Optimistically update the query data
                const patchResult = dispatch(
                    itemTrackerAPI.util.updateQueryData('getItemTrackers', item.queryParams, (draft) => {
                        const tracker = draft.find(t => t.id === item.id);
                        if (tracker) {
                            const updates = {
                                ...(item.disposition !== undefined && { disposition: item.disposition }),
                                ...(item.notes !== undefined && { notes: item.notes }),
                                ...(item.isHidden !== undefined && { isHidden: item.isHidden })
                            };
                            Object.assign(tracker, updates);
                        }
                    })
                );
                try {
                    await queryFulfilled;
                } catch {
                    // If the mutation fails, revert the optimistic update
                    patchResult.undo();
                    dispatch(itemTrackerAPI.util.invalidateTags([{ type: 'ItemTracker', id: 'LIVE' }]));
                }
            }
        }),
        updateItemTrackerPaginated: builder.mutation<Omit<UpdateItemTrackerParams, 'id'>, UpdateItemTrackerParams & { queryParams?: GetAllItemTrackersByDateRangeParams }>({
            query: (item) => ({
                url: `ItemTrackers/${item.id}`,
                method: 'PUT',
                body: {
                    disposition: item.disposition,
                    notes: item.notes,
                }
            }),
            async onQueryStarted(item, { dispatch, queryFulfilled }) {
                if (!item.queryParams) {
                    try {
                        await queryFulfilled;
                    } catch {
                    }
                    return;
                }

                // Optimistically update the query data
                const patchResult = dispatch(
                    itemTrackerAPI.util.updateQueryData('getAllItemTrackers', item.queryParams, (draft) => {
                        const tracker = draft.rows.find(t => t.id === item.id);
                        if (tracker) {
                            const updates = {
                                ...(item.disposition !== undefined && { disposition: item.disposition }),
                                ...(item.notes !== undefined && { notes: item.notes }),
                            };
                            Object.assign(tracker, updates);
                        }
                    })
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                    dispatch(itemTrackerAPI.util.invalidateTags([{ type: 'ItemTracker', id: 'LIST' }]));
                }
            }
        }),

    }),
});

export const {
    useGetItemTrackersQuery,
    useUpdateItemTrackerMutation,
    useLazyGetAllItemTrackersQuery,
    useUpdateItemTrackerPaginatedMutation
} = itemTrackerAPI;
